import { useState } from 'react';
import { Plus, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { usePermissions } from '../hooks/usePermissions';

const normalizeName = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .replace(/\s+/g, ' ')
  .toLowerCase();

export const AssignedUserSelect = ({ value, onValueChange, users = [], onUserCreated }) => {
  const { canCreate } = usePermissions('config', 'users');
  const [newUserName, setNewUserName] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);

  const handleCreateUser = async () => {
    const name = newUserName.trim().replace(/\s+/g, ' ');
    if (!name) {
      toast.error('Ingresá el nombre del usuario');
      return;
    }

    const existingUser = users.find((user) => normalizeName(user.name || user.nombre) === normalizeName(name));
    if (existingUser) {
      onValueChange(String(existingUser.id));
      setNewUserName('');
      toast.info('El usuario ya existía y fue seleccionado');
      return;
    }

    setCreatingUser(true);
    try {
      const response = await api.post('/config/usuarios', { name });
      const createdUser = { id: response.data.id, name, nombre: name };
      onUserCreated?.(createdUser);
      onValueChange(String(createdUser.id));
      setNewUserName('');
      toast.success('Usuario creado y asignado al celular');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo crear el usuario');
    } finally {
      setCreatingUser(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>Usuario Asignado</Label>
      <Select value={String(value || '')} onValueChange={onValueChange}>
        <SelectTrigger><SelectValue placeholder="Seleccionar usuario..." /></SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {users.map((user) => (
            <SelectItem key={user.id} value={String(user.id)}>{user.nombre}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {canCreate && (
        <div className="flex gap-2 pt-1">
          <Input
            value={newUserName}
            onChange={(event) => setNewUserName(event.target.value)}
            placeholder="Nombre de un usuario nuevo"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleCreateUser();
              }
            }}
          />
          <Button type="button" variant="outline" className="shrink-0" onClick={handleCreateUser} disabled={creatingUser || !newUserName.trim()}>
            <Plus className="mr-1 h-4 w-4" strokeWidth={1.5} />
            {creatingUser ? 'Creando...' : 'Crear'}
          </Button>
        </div>
      )}
      {canCreate && (
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <User className="h-3 w-3" strokeWidth={1.5} />
          Se crea solo con el nombre y queda asignado.
        </p>
      )}
    </div>
  );
};
